import type { VictoryId } from '@sd/content';
import { t, type Locale } from '@sd/i18n';
import type { GameState } from '@sd/sim';
import { METRICS, type Metric, type Sample } from '../game/history';
import type { Viewer } from '../map/describe';
import { clock, escapeHtml, num, playerName, seatColor } from './format';

/**
 * The end-of-match screen (#59): who won and how, the Prestige table, and
 * each player's economy over the match, from the history sampled once a
 * cycle (game/history.ts). Pure: strings out.
 */
export interface StandingRow {
  readonly place: number;
  readonly id: string;
  readonly name: string;
  readonly faction: string;
  readonly color: string;
  readonly prestige: number;
  readonly you: boolean;
  readonly winner: boolean;
}

export interface Chart {
  readonly metric: Metric;
  readonly title: string;
  readonly note: string;
  readonly series: readonly {
    readonly id: string;
    readonly name: string;
    readonly color: string;
    readonly values: readonly number[];
  }[];
  readonly max: number;
  readonly cycles: number;
}

export interface SummaryView {
  readonly outcome: 'won' | 'lost' | 'collapse';
  readonly title: string;
  readonly victory: string;
  readonly how: string;
  readonly duration: string;
  readonly rows: readonly StandingRow[];
  readonly charts: readonly Chart[];
}

export function summaryView(
  state: GameState,
  v: Viewer,
  samples: readonly Sample[],
): SummaryView | null {
  const result = state.result;
  if (!result) return null;
  const { locale, me } = v;
  const name = (id: string) => playerName(locale, v.setup, id);
  const winner = result.winner;
  // With teams (#137) every teammate of the winner won too.
  const winners = new Set(result.winners);
  const outcome = winner === null ? 'collapse' : winners.has(me) ? 'won' : 'lost';
  const kind: VictoryId = result.kind;

  const rows = result.standings.map((s, i): StandingRow => {
    const p = state.players.find((x) => x.id === s.playerId)!;
    return {
      place: i + 1,
      id: s.playerId,
      name: name(s.playerId),
      faction: t(locale, `faction.${p.faction}.name`),
      color: seatColor(s.playerId),
      prestige: s.prestige,
      you: s.playerId === me,
      winner: winners.has(s.playerId),
    };
  });

  const series = (metric: Metric) =>
    rows.map((r) => ({
      id: r.id,
      name: r.name,
      color: r.color,
      values: samples.map((x) => x.players[r.id]?.[metric] ?? 0),
    }));
  const charts = METRICS.map((metric): Chart => {
    const s = series(metric);
    return {
      metric,
      title: t(locale, `summary.chart.${metric}`),
      note: t(locale, `summary.chart.${metric}.note`),
      series: s,
      max: Math.max(1, ...s.flatMap((x) => x.values)),
      cycles: samples.length,
    };
  });

  return {
    outcome,
    title:
      outcome === 'won'
        ? t(locale, 'summary.won')
        : outcome === 'lost'
          ? t(locale, 'summary.lost', { name: [...winners].map(name).join(', ') })
          : t(locale, 'summary.collapse'),
    victory: t(locale, `victory.${kind}.name`),
    how: t(locale, `summary.how.${kind}`, { name: winner ? name(winner) : '' }),
    duration: t(locale, 'summary.duration', { time: clock(result.tick) }),
    rows,
    charts,
  };
}

/** A line chart of one metric, a line per player in their seat colour. */
export function chartSvg(locale: Locale, chart: Chart): string {
  const w = 300;
  const h = 120;
  const pad = 4;
  const n = Math.max(2, chart.cycles);
  const x = (i: number) => pad + (i / (n - 1)) * (w - 2 * pad);
  const y = (value: number) => h - pad - (value / chart.max) * (h - 2 * pad);
  const lines = chart.series
    .map((s) => {
      if (s.values.length === 0) return '';
      const points = (s.values.length === 1 ? [s.values[0]!, s.values[0]!] : s.values)
        .map((value, i) => `${x(i).toFixed(1)},${y(value).toFixed(1)}`)
        .join(' ');
      return `<polyline points="${points}" fill="none" stroke="${s.color}" stroke-width="2" stroke-linejoin="round"/>`;
    })
    .join('');
  return `<svg class="sd-chart" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" role="img" aria-label="${escapeHtml(chart.title)}">
    <line x1="${pad}" y1="${h - pad}" x2="${w - pad}" y2="${h - pad}" class="sd-chart__axis"/>
    <line x1="${pad}" y1="${pad}" x2="${w - pad}" y2="${pad}" class="sd-chart__grid"/>
    ${lines}
  </svg><div class="sd-chart__scale"><span>${t(locale, 'summary.chart.max', { n: num(locale, chart.max, chart.max < 10 ? 1 : 0) })}</span><span>${t(locale, 'summary.chart.cycles', { n: chart.cycles })}</span></div>`;
}

/** The end-of-match dialog body: the outcome, the Prestige table and the charts. */
export function summaryHtml(locale: Locale, view: SummaryView): string {
  const rows = view.rows
    .map(
      (r) => `<tr class="${r.you ? 'sd-row--you' : ''}">
        <td>${r.place}</td>
        <td><span class="sd-good"><span class="sd-emblem sd-emblem--sm" style="--seat:${r.color}"></span>${escapeHtml(r.name)} <small class="sd-muted">${escapeHtml(r.faction)}</small></span>${r.winner ? ` <span class="sd-badge sd-badge--you">${t(locale, 'summary.winnerBadge')}</span>` : ''}</td>
        <td><b>${num(locale, r.prestige, 1)}</b></td>
      </tr>`,
    )
    .join('');
  const legend = view.rows
    .map(
      (r) =>
        `<span class="sd-rate"><span class="sd-emblem sd-emblem--sm" style="--seat:${r.color}"></span>${escapeHtml(r.name)}</span>`,
    )
    .join('');
  const charts = view.charts
    .map(
      (c) => `<figure class="sd-chart-box">
        <figcaption><b>${escapeHtml(c.title)}</b><small class="sd-muted">${escapeHtml(c.note)}</small></figcaption>
        ${chartSvg(locale, c)}
      </figure>`,
    )
    .join('');
  return `<div class="sd-summary sd-summary--${view.outcome}">
    <p class="sd-summary__victory"><b>${escapeHtml(view.victory)}</b> · <span class="sd-muted">${escapeHtml(view.duration)}</span></p>
    <p class="sd-summary__how">${escapeHtml(view.how)}</p>
    <div class="sd-summary__grid">
      <section>
        <h3>${t(locale, 'summary.standings')}</h3>
        <table class="sd-table"><thead><tr><th>${t(locale, 'summary.col.place')}</th><th>${t(locale, 'summary.col.player')}</th><th>${t(locale, 'summary.col.prestige')}</th></tr></thead><tbody>${rows}</tbody></table>
      </section>
      <section>
        <h3>${t(locale, 'summary.economy')}</h3>
        <div class="sd-rates">${legend}</div>
        <div class="sd-charts">${charts}</div>
      </section>
    </div>
  </div>`;
}
